import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { xosoService } from "@/services/xosoService";
import { PRIZE_SLOT_COUNTS } from "./constants";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const stores = {};

function todayStr() {
  return dayjs().tz(TZ).format("YYYY-MM-DD");
}

function parseLiveEvent(event) {
  try {
    const data = JSON.parse(event.data);
    if (!data || typeof data !== "object") return null;
    if (!data.kind) {
      if (Array.isArray(data.changes)) data.kind = "update";
      else if (Array.isArray(data.provinces) && data.drawDate) data.kind = "snapshot";
      else if (Array.isArray(data.provinces)) data.kind = "schedule";
    }
    return data;
  } catch {
    return null;
  }
}

function emptyState(region) {
  return { region, schedule: [], drawDate: null, byCode: {} };
}

function clonePrizes(prizes) {
  return { ...(prizes || {}) };
}

function reduce(state, event, today) {
  if (!event || (event.region && event.region !== state.region)) return state;

  if (event.kind === "schedule") {
    return { ...state, schedule: event.provinces || [] };
  }

  if (event.kind === "snapshot") {
    if (event.drawDate !== today) return state;
    const byCode = {};
    (event.provinces || []).forEach((item) => {
      const code = item.provinceCode || item.province?.code;
      if (!code) return;
      byCode[code] = {
        provinceCode: code,
        provinceName: item.provinceName || item.province?.name,
        prizes: clonePrizes(item.prizes),
      };
    });
    return { ...state, drawDate: event.drawDate, byCode };
  }

  if (event.kind === "update") {
    if (event.drawDate && event.drawDate !== today) return state;
    const byCode = { ...state.byCode };
    (event.changes || []).forEach((change) => {
      const code = change.provinceCode || change.province?.code;
      if (!code) return;
      const prev = byCode[code] || {
        provinceCode: code,
        provinceName: change.provinceName,
        prizes: {},
      };
      byCode[code] = {
        provinceCode: code,
        provinceName: change.provinceName || change.province?.name || prev.provinceName,
        prizes: { ...prev.prizes, ...(change.newPrizes || change.prizes || {}) },
      };
    });
    return { ...state, drawDate: event.drawDate || state.drawDate || today, byCode };
  }

  return state;
}

function orderedProvinces(state) {
  if (state.schedule.length) {
    return state.schedule.map((item) => {
      const code = item.provinceCode;
      return (
        state.byCode[code] || {
          provinceCode: code,
          provinceName: item.provinceName,
          prizes: {},
        }
      );
    });
  }
  return Object.values(state.byCode);
}

function getStore(region) {
  if (stores[region]) return stores[region];

  const store = {
    state: emptyState(region),
    listeners: new Set(),
    es: null,
    lastEventId: "",
    closeTimer: null,
  };

  const es = new EventSource(xosoService.getLiveStreamUrl(region));
  const handle = (event) => {
    const id = event.lastEventId || "";
    if (id && id === store.lastEventId) return;
    if (id) store.lastEventId = id;
    const data = parseLiveEvent(event);
    if (!data) return;
    const next = reduce(store.state, data, todayStr());
    if (next === store.state) return;
    store.state = next;
    store.listeners.forEach((fn) => fn(store.state));
  };
  es.addEventListener("schedule", handle);
  es.addEventListener("snapshot", handle);
  es.addEventListener("update", handle);
  es.onmessage = handle;
  store.es = es;
  stores[region] = store;
  return store;
}

export function subscribeXosoLive(region, callback) {
  const store = getStore(region);
  if (store.closeTimer) {
    clearTimeout(store.closeTimer);
    store.closeTimer = null;
  }
  store.listeners.add(callback);
  callback(store.state);
  return () => {
    store.listeners.delete(callback);
    if (store.listeners.size === 0) {
      store.closeTimer = setTimeout(() => {
        if (store.listeners.size === 0 && stores[region] === store) {
          store.es.close();
          delete stores[region];
        }
      }, 1500);
    }
  };
}

function mapLiveResults(provinces, region, drawDate) {
  return (provinces || []).map((item) => ({
    id: item.provinceCode,
    provinceId: item.provinceCode,
    region,
    drawDate,
    prizes: item.prizes || {},
    province: {
      code: item.provinceCode,
      name: item.provinceName,
      region,
    },
  }));
}

function hasAnyNumbers(provinces) {
  return (provinces || []).some((item) =>
    Object.values(item.prizes || {}).some((nums) => Array.isArray(nums) && nums.length)
  );
}

export function isLiveComplete(provinces, region) {
  const slots = PRIZE_SLOT_COUNTS[region];
  if (!provinces?.length || !slots) return false;
  return provinces.every((item) =>
    Object.entries(slots).every(([key, count]) => (item.prizes?.[key]?.length || 0) >= count)
  );
}

function inDrawWindow(scheduledTime) {
  if (!scheduledTime) return false;
  const [hh, mm] = String(scheduledTime)
    .split(":")
    .map((part) => Number(part));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
  const now = dayjs().tz(TZ);
  const start = now.hour(hh).minute(mm).second(0).millisecond(0);
  return now.isAfter(start.subtract(20, "second")) && now.isBefore(start.add(90, "minute"));
}

export function selectLiveResults(rest, live) {
  if (live?.results?.length) return live.results;
  return rest;
}

export default function useXosoLive(region, enabled) {
  const [board, setBoard] = useState(() => emptyState(region));
  const [status, setStatus] = useState(null);
  const stickyRef = useRef({ region: null, results: null });

  useEffect(() => {
    if (!enabled || !region) {
      setBoard(emptyState(region));
      setStatus(null);
      return undefined;
    }
    return subscribeXosoLive(region, setBoard);
  }, [region, enabled]);

  useEffect(() => {
    if (!enabled || !region) return undefined;
    let cancelled = false;
    let timer;
    setStatus(null);

    const poll = async () => {
      const data = await xosoService.getLiveStatus(region);
      if (cancelled) return;
      if (data) setStatus({ ...data, region, fetchedAt: Date.now() });
      const remaining = data?.secondsRemaining;
      const wait = remaining == null ? 20000 : remaining > 90 ? 15000 : 4000;
      timer = setTimeout(poll, wait);
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [region, enabled]);

  return useMemo(() => {
    const today = todayStr();
    const boardForRegion = board.region === region ? board : emptyState(region);
    const liveStatus = status?.region === region ? status : null;
    const elapsed = liveStatus?.fetchedAt ? Math.floor((Date.now() - liveStatus.fetchedAt) / 1000) : 0;
    const remaining =
      !liveStatus || liveStatus.secondsRemaining == null
        ? liveStatus
          ? null
          : undefined
        : Math.max(0, liveStatus.secondsRemaining - elapsed);
    const columns = orderedProvinces(boardForRegion);
    const liveToday = boardForRegion.drawDate === today;
    const complete = liveToday && isLiveComplete(columns, region);
    const windowOpen = inDrawWindow(liveStatus?.scheduledTime);
    const countdownHit = windowOpen && (remaining === 0 || remaining === null);
    const drawing =
      Boolean(enabled) &&
      !complete &&
      (countdownHit || (liveToday && hasAnyNumbers(columns)));

    let results = null;
    if (liveToday && columns.length) {
      results = mapLiveResults(columns, region, boardForRegion.drawDate);
    } else if (drawing && boardForRegion.schedule.length) {
      results = mapLiveResults(columns, region, today);
    }

    if (stickyRef.current.region !== region) {
      stickyRef.current = { region, results: null };
    }
    if (results) stickyRef.current.results = results;
    else if (enabled && stickyRef.current.results) results = stickyRef.current.results;

    return {
      drawing,
      complete,
      results,
      scheduledTime: liveStatus?.scheduledTime || null,
      secondsRemaining: remaining,
    };
  }, [enabled, region, board, status]);
}
